(ns kyber.components.composite.pdf-viewer
  (:require [clojure.string :as str]
            [reagent.core :as reagent]
            [re-frame.core :as re-frame]

            [kyber.components.base.button :refer [icon-button-sm
                                                  icon-button-alt-sm
                                                  icon-button-lg
                                                  button-group-sm]]
            [kyber.components.base.text :refer [body-20]]
            [kyber.components.composite.event-tracker :refer [event-tracker]]
            [kyber.utils :as ku]

            [kyber.styles.core :refer [styles->classes gs]]
            [kyber.i18n.core :as i18n]))

(def pdf-control-bar-height "50px")
(def search-box-width "170px")

;; Values must add up to 100%
;; These percentages are only used on devices larger than tablet
(def page-container-width "28%")
(def search-box-container-width "36%")
(def scale-controls-container-width "36%")

(def scale-classes
  (styles->classes
   {:scale-pdf-container
    {:display :flex
     :justify-content :flex-end
     :width scale-controls-container-width

     (gs [:queries :tablet])
     {:width :initial}

     (gs [:queries :mobile-landscape])
     {:display :none}}

    :zoom-in-btn
    {:margin-left (gs [:spacing :p4])}

    :scale-results
    {:margin [["auto" (gs [:spacing :p4])]]}}))

(defn- attach-event-tracker
  [component event-map]
  (conj [event-tracker event-map] component))

(defn- scale-controls
  [{:keys [current-scale]} attach-events]
  [:div {:class (:scale-pdf-container scale-classes)}
   [body-20
    :p
    {:class (:scale-results scale-classes)}
    (str (.round js/Math (* current-scale 100)) "%")]

   (cond-> [icon-button-sm
            {:on-click #(re-frame/dispatch
                         [:pdf-viewer/update-scale {:dir -1}])}
            :minus]
     (true? attach-events)
     (attach-event-tracker {:var #'icon-button-sm
                            :tag :pdf-viewer-reduce-scale-button}))


   (cond-> [icon-button-sm
            {:on-click #(re-frame/dispatch [:pdf-viewer/update-scale {:dir 1}])
             :class (:zoom-in-btn scale-classes)}
            :plus]
     (true? attach-events)
     (attach-event-tracker {:var #'icon-button-sm
                            :tag :pdf-viewer-increase-scale-button}))])

(defn- track-search
  [value ecid]
  (re-frame/dispatch [:analytics/save-datapoint {:event :pdf-viewer/search-text
                                                 :event-version 0
                                                 :ecid ecid
                                                 :search value}]))

(def debounced-track-search
  ;; Same as pdfjs' debounce
  (ku/debounce track-search 250))

(def search-classes
  (styles->classes
   {:search-box-container
    {:display :flex
     :justify-content :flex-start
     :align-items :center
     :position :relative
     :padding-left (gs [:spacing :p12])
     :width search-box-container-width
     (gs [:queries :tablet]) {:width :initial
                              :position :absolute
                              :left (gs [:spacing :p0])}}

    :search-box
    {:background :transparent
     :padding [[(gs [:spacing :p4])
                (gs [:spacing :p8])
                (gs [:spacing :p4])
                (gs [:spacing :p28])]]
     :box-sizing :border-box
     :border-radius "100px"
     :border (str "1px solid " (gs [:colors :gray-scale-200]))
     :width search-box-width
     :height (gs [:spacing :p32])
     :margin (gs [:spacing :p0])
     :outline 0
     "-webkit-appearance" :textfield
     "&::placeholder" {:font-weight (gs [:font-weight :body-10])}}

    :search-buttons
    {:margin [["auto" (gs [:spacing :p4])]]}}))

(defn- search-box
  "Document search & search navigation UI"
  [{:keys [selected-search-result total-search-results ecid search-api]}
   attach-events]
  [:div {:class (:search-box-container search-classes)}
   [:i {:class "fas fa-search "
        :style {:padding-right (gs [:spacing :p8])
                :margin-left (gs [:spacing :p4])}}]

   (cond-> [:input {:type :search
                    :class (:search-box search-classes)
                    :placeholder (i18n/t* ::search-placeholder)
                    :on-change (fn [e]
                                 (let [value (-> e .-target .-value)]
                                   (re-frame/dispatch
                                    [:pdf-viewer/search-text value])

                                   (debounced-track-search value ecid)))}]
     (true? attach-events)
     (attach-event-tracker {:var {:doc "Raw Input"
                                  :id :input
                                  :version 0
                                  :variant :a}
                            :tag :pdf-viewer-search-box}))


   [button-group-sm
    {:class (:search-buttons search-classes)
     :left-button {:icon :chevron-left
                   :options {:on-click
                             #(re-frame/dispatch [:pdf-viewer/previous-result])

                             :disabled
                             (or
                              (nil? selected-search-result)
                              (<= (inc (:result selected-search-result)) 1))}}
     :right-button {:icon :chevron-right
                    :options {:on-click
                              #(re-frame/dispatch [:pdf-viewer/next-result])

                              :disabled
                              (or
                               (nil? selected-search-result)
                               (>= (inc (:result selected-search-result))
                                   total-search-results))}}}]

   (let [search-value (or (and search-api
                               (.-state search-api)
                               (-> search-api .-state .-query))
                          "")]

     (when-not (str/blank? search-value)
       [body-20 :p
        {:style {:margin "auto" :white-space :nowrap}}
        (if (pos? total-search-results)
          (i18n/t* ::search-result-counter
                   (inc (get selected-search-result :result -1))
                   total-search-results)
         (i18n/t* ::search-result-none))]))])

(def page-control-classes
  (styles->classes
   {:pdf-page-controls
    {:display :flex
     :justify-content :center
     :align-items :middle
     :width page-container-width
     (gs [:queries :tablet]) {:display :none}}

    :page-divider
    {:color (gs [:colors :gray-scale-200])}

    :page-controls-current-page
    {:margin (gs [:spacing :p0])
     :display :flex
     :justify-content :center
     :align-items :center}}))

(defn- page-controls
  "Next / Previous page controls"
  [{:keys [current-page total-pages]} attach-events]
  [:div {:class (:pdf-page-controls page-control-classes)}

   (cond->
    [icon-button-alt-sm
     {:on-click #(re-frame/dispatch [:pdf-viewer/previous-page])
      :disabled (<= current-page 1)}
     :chevron-left]
     (true? attach-events)
     (attach-event-tracker {:var #'icon-button-alt-sm
                            :tag :pdf-viewer-previous-page-button}))

   [body-20
    :p {:class (:page-controls-current-page page-control-classes)}
    [:span
     [:span current-page]
     [:span {:class (:page-divider page-control-classes)} " / "]
     [:span total-pages]]]

   (cond-> [icon-button-alt-sm
            {:on-click #(re-frame/dispatch [:pdf-viewer/next-page])
             :disabled (>= current-page total-pages)}
            :chevron-right]
     (true? attach-events)
     (attach-event-tracker {:var #'icon-button-alt-sm
                            :tag :pdf-viewer-next-page-button}))])

(def pdf-control-bar-classes
  (styles->classes
   {:pdf-control-bar
    {:position :relative
     :display :flex
     :justify-content :space-between
     :align-items :center
     :height pdf-control-bar-height
     :width "100%"
     :background (gs [:colors :true-white])
     :border-top (str "2px solid " (gs [:colors :gray-scale-200]))

     (gs [:queries :tablet])
     {:justify-content :flex-end}}

    :close-button
    {:display :none

     (gs [:queries :mobile-landscape])
     {:display :initial
      :margin-right (gs [:spacing :p16])}}}))

(defn pdf-controls
  [attach-events]
  (let [pdf-viewer (re-frame/subscribe [:pdf-viewer])]
    (fn []
      [:div {:class (:pdf-control-bar pdf-control-bar-classes)}
       [search-box @pdf-viewer attach-events]
       [page-controls @pdf-viewer attach-events]
       [scale-controls @pdf-viewer attach-events]

       (cond-> [icon-button-lg
                {:on-click #(re-frame/dispatch
                             [:resource-library/close-resource-viewer])
                 :class (:close-button pdf-control-bar-classes)} :close]
         (true? attach-events)
         (attach-event-tracker {:var #'icon-button-lg
                                :tag :resource-viewer-close-button}))])))

(defn- update-scroll
  [_]
  (re-frame/dispatch [:pdf-viewer/scroll-page]))

(defn- on-scroll
  [ecid]
  (fn [_]
    (.requestAnimationFrame js/window #(update-scroll ecid))))

(defn- update-scale-for-resize
  [_]
  (re-frame/dispatch [:pdf-viewer/set-scale-to-fit]))

(def ^{:private true} on-resize
  (fn []
    (.requestAnimationFrame js/window update-scale-for-resize)))

(defn component-will-unmount
  [ecid]
  (fn [_]
    (re-frame/dispatch [:pdf-viewer/close
                        {:analytic/data {:ecid @ecid}}])

    (re-frame/dispatch [:analytics/remove-event-context
                        {:ecid @ecid}])

    (.removeEventListener js/window "resize" on-resize)))

(def pdf-container-classes
  (styles->classes
   {:pdf-container
    {:display :flex
     :flex-direction :column
     :height "100%"
     :width "100%"}

    :pdf-viewing-container
    {:height "100%"
     :width "100%"
     :overflow :hidden
     :background-color (gs [:colors :gray-scale-200])
     :border (str "1px solid " (gs [:colors :gray-scale-200]))}

    :pdf-viewing-container-inner-wrapper
    {:position :relative
     :height "100%"
     :width "100%"
     :overflow :auto
     :padding-right "28px"
     "& .page" {:border-image :none
                :border :none
                :margin [[(gs [:spacing :p8]) "auto"]]
                :box-shadow (gs [:shadows :shadow-24])}

     (gs [:queries :mobile-landscape])
     {:padding-right (gs [:spacing :p0])}}}))

(defn render
  [pdf-resource {:keys [attach-events]
                 :or {attach-events true}}]
  (let [!ref (atom nil)
        ecid (atom (ku/gen-cid))
        ns-pdf-resource (if (some? (:resource/uuid pdf-resource))
                          pdf-resource
                          (ku/map->nsmap :resource pdf-resource))]
    (reagent/create-class
     {:component-did-mount
      (fn [_]
        (.addEventListener js/window "resize" on-resize)
        (re-frame/dispatch [:analytics/set-event-context
                            {:ecid @ecid
                             :event-context
                             {:resource-uuid (:resource/uuid ns-pdf-resource)}}])

        (re-frame/dispatch [:pdf-viewer/initialize {:resource ns-pdf-resource
                                                    :container @!ref
                                                    :ecid @ecid}]))
      :component-will-unmount
      (component-will-unmount ecid)
      :reagent-render
      (fn []
        [:div {:class (:pdf-container pdf-container-classes)}
         [:div {:class (:pdf-viewing-container pdf-container-classes)}
          [:div {:ref #(reset! !ref %)
                 :on-scroll (on-scroll @ecid)
                 :class (:pdf-viewing-container-inner-wrapper
                         pdf-container-classes)}
           [:div {:class "pdfViewer"}]]]
         [pdf-controls attach-events]])})))
